import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Briefcase, Home, Clock, Eye, Heart, ChevronLeft, ChevronRight, Share2, Facebook, Twitter, Linkedin, Copy, Check, Edit, ArrowLeft, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import Footer from '@/components/Footer';
import { API } from '../App';

const BlogPostPage = ({ user }) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [allPosts, setAllPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchPost();
    fetchAllPosts();
  }, [slug]);

  const fetchPost = async () => {
    try {
      const response = await fetch(`${API}/blog/post/${slug}`);
      if (response.ok) {
        const data = await response.json();
        setPost(data);
      } else {
        navigate('/blog');
      }
    } catch (error) {
      console.error('Error fetching post:', error);
      navigate('/blog');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllPosts = async () => {
    try {
      const response = await fetch(`${API}/blog`);
      if (response.ok) {
        const data = await response.json();
        setAllPosts(data);
      }
    } catch (error) {
      console.error('Error fetching all posts:', error);
    }
  };

  // Get previous and next posts
  const currentIndex = allPosts.findIndex(p => p.slug === slug);
  const prevPost = currentIndex > 0 ? allPosts[currentIndex - 1] : null;
  const nextPost = currentIndex < allPosts.length - 1 ? allPosts[currentIndex + 1] : null;

  const handleLike = async () => {
    if (liked) return;
    try {
      const response = await fetch(`${API}/blog/${post.id}/like`, { method: 'POST' });
      if (response.ok) {
        const data = await response.json();
        setPost({ ...post, likes: data.likes });
        setLiked(true);
        toast.success('¡Gracias por tu like!');
      }
    } catch (error) {
      console.error('Error liking post:', error);
    }
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success('Enlace copiado');
    setTimeout(() => setCopied(false), 2000);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { 
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getCategoryColor = (category) => {
    const colors = {
      consejos: 'bg-blue-100 text-blue-700',
      guias: 'bg-emerald-100 text-emerald-700',
      noticias: 'bg-amber-100 text-amber-700',
      testimonios: 'bg-purple-100 text-purple-700'
    };
    return colors[category] || 'bg-slate-100 text-slate-700';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  if (!post) return null;

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-sm z-50 border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            <Link to="/" className="flex items-center space-x-2">
              <Briefcase className="w-6 h-6 sm:w-8 sm:h-8 text-red-600" />
              <span className="text-lg sm:text-2xl font-bold text-slate-900">JobSuiza</span>
            </Link>

            <div className="flex items-center space-x-2 sm:space-x-4">
              <Link to="/blog">
                <Button variant="ghost" size="sm">
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Blog
                </Button>
              </Link>
              <Link to="/ofertas">
                <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white">
                  Ofertas
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Article */}
      <article className="pt-20 sm:pt-24">
        {/* Header */}
        <header className="px-4 py-8 sm:py-12 bg-gradient-to-b from-slate-50 to-white">
          <div className="max-w-3xl mx-auto">
            {/* Back link */}
            <Link 
              to="/blog" 
              className="inline-flex items-center text-slate-500 hover:text-red-600 text-sm mb-6 transition-colors"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Volver al blog
            </Link>

            {/* Category */}
            <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide mb-4 ${getCategoryColor(post.category)}`}>
              {post.category}
            </span>

            {/* Title */}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight mb-6">
              {post.title}
            </h1>

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-4 text-slate-500 mb-6">
              <span className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" />
                {formatDate(post.created_at)}
              </span>
              <span className="flex items-center gap-1.5">
                <Eye className="w-4 h-4" />
                {post.views || 0} lecturas
              </span>
              <span className="flex items-center gap-1.5">
                <Heart className="w-4 h-4" />
                {post.likes || 0} likes
              </span>
              {post.author_name && (
                <span className="text-sm">
                  Por <strong className="text-slate-700">{post.author_name}</strong>
                </span>
              )}
            </div>

            {/* Admin Edit Button */}
            {user?.role === 'admin' && (
              <Link to={`/blog/editor/${post.id}`}>
                <Button variant="outline" size="sm" className="mb-4">
                  <Edit className="w-4 h-4 mr-2" />
                  Editar artículo
                </Button>
              </Link>
            )}
          </div>
        </header>

        {/* Cover Image */}
        {post.cover_image && (
          <div className="max-w-4xl mx-auto px-4 mb-10">
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img 
                src={post.cover_image} 
                alt={post.title}
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
        )}

        {/* Content */}
        <div className="px-4 pb-12">
          <div className="max-w-3xl mx-auto">
            {/* Article Content - Custom styled */}
            <div 
              className="blog-content"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />

            {/* Add blog content styles */}
            <style>{`
              .blog-content {
                color: #334155;
                font-size: 1.125rem;
                line-height: 1.8;
              }
              .blog-content h2 {
                font-size: 1.75rem;
                font-weight: 700;
                color: #0f172a;
                margin-top: 2.5rem;
                margin-bottom: 1rem;
                padding-bottom: 0.5rem;
                border-bottom: 2px solid #e2e8f0;
              }
              .blog-content h3 {
                font-size: 1.375rem;
                font-weight: 600;
                color: #1e293b;
                margin-top: 2rem;
                margin-bottom: 0.75rem;
              }
              .blog-content p {
                margin-bottom: 1.5rem;
                color: #475569;
              }
              .blog-content ul, .blog-content ol {
                margin: 1.5rem 0;
                padding-left: 1.5rem;
              }
              .blog-content li {
                margin-bottom: 0.5rem;
                color: #475569;
              }
              .blog-content ul li {
                list-style-type: disc;
              }
              .blog-content ol li {
                list-style-type: decimal;
              }
              .blog-content strong {
                font-weight: 600;
                color: #1e293b;
              }
              .blog-content blockquote {
                border-left: 4px solid #dc2626;
                background: #f8fafc;
                padding: 1rem 1.5rem;
                margin: 1.5rem 0;
                border-radius: 0 0.5rem 0.5rem 0;
                font-style: normal;
              }
              .blog-content a {
                color: #dc2626;
                text-decoration: none;
              }
              .blog-content a:hover {
                text-decoration: underline;
              }
              .blog-content img {
                border-radius: 0.75rem;
                margin: 1.5rem 0;
                box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
              }
            `}</style>

            {/* Divider */}
            <div className="border-t border-slate-200 my-12"></div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
              {/* Like Button */}
              <button
                onClick={handleLike}
                disabled={liked}
                className={`flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all ${
                  liked 
                    ? 'bg-red-100 text-red-600 cursor-default'
                    : 'bg-slate-100 text-slate-700 hover:bg-red-100 hover:text-red-600'
                }`}
                data-testid="like-btn"
              >
                <Heart className={`w-5 h-5 ${liked ? 'fill-current' : ''}`} />
                {liked ? '¡Gracias!' : 'Me gusta este artículo'}
              </button>

              {/* Share Buttons */}
              <div className="flex items-center gap-3">
                <span className="text-sm text-slate-500 mr-2">Compartir:</span>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                  aria-label="Compartir en Facebook"
                >
                  <Facebook className="w-5 h-5" />
                </a>
                <a
                  href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(post.title)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-900 text-white hover:bg-slate-800 transition-colors"
                  aria-label="Compartir en Twitter"
                >
                  <Twitter className="w-5 h-5" />
                </a>
                <a
                  href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(post.title)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-blue-700 text-white hover:bg-blue-800 transition-colors"
                  aria-label="Compartir en LinkedIn"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
                <button
                  onClick={handleCopyLink}
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-slate-200 text-slate-700 hover:bg-slate-300 transition-colors"
                  aria-label="Copiar enlace"
                >
                  {copied ? <Check className="w-5 h-5 text-emerald-600" /> : <Copy className="w-5 h-5" />}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Buttons */}
        {(prevPost || nextPost) && (
          <div className="px-4 py-8 border-t border-slate-100">
            <div className="max-w-3xl mx-auto">
              <div className="flex flex-col sm:flex-row gap-4 justify-between">
                {/* Previous Article */}
                {prevPost ? (
                  <Link 
                    to={`/blog/${prevPost.slug}`}
                    className="flex-1 group p-4 rounded-xl border border-slate-200 hover:border-red-300 hover:bg-red-50 transition-all"
                  >
                    <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                      <ArrowLeft className="w-4 h-4" />
                      <span>Artículo anterior</span>
                    </div>
                    <h3 className="font-semibold text-slate-900 group-hover:text-red-600 transition-colors line-clamp-2">
                      {prevPost.title}
                    </h3>
                  </Link>
                ) : (
                  <div className="flex-1" />
                )}

                {/* Next Article */}
                {nextPost ? (
                  <Link 
                    to={`/blog/${nextPost.slug}`}
                    className="flex-1 group p-4 rounded-xl border border-slate-200 hover:border-red-300 hover:bg-red-50 transition-all text-right"
                  >
                    <div className="flex items-center justify-end gap-2 text-sm text-slate-500 mb-2">
                      <span>Siguiente artículo</span>
                      <ArrowRight className="w-4 h-4" />
                    </div>
                    <h3 className="font-semibold text-slate-900 group-hover:text-red-600 transition-colors line-clamp-2">
                      {nextPost.title}
                    </h3>
                  </Link>
                ) : (
                  <div className="flex-1" />
                )}
              </div>
            </div>
          </div>
        )}

        {/* CTA Section */}
        <section className="py-12 px-4 bg-slate-50">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">
              ¿Buscas trabajo en Suiza?
            </h2>
            <p className="text-slate-600 mb-6">
              Explora cientos de ofertas de trabajo que buscan hispanohablantes
            </p>
            <Link to="/ofertas">
              <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white px-8">
                Ver Ofertas de Trabajo
              </Button>
            </Link>
          </div>
        </section>
      </article>

      <Footer />
    </div>
  );
};

export default BlogPostPage;
